const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 50;
const SESSION_KEY = "wb_analytics_session";
const API_URL = process.env.REACT_APP_BASE_URL;

let eventQueue = [];
let flushTimer = null;
let isInitialized = false;

const getSessionId = () => {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid =
      Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const getUserId = () => {
  try {
    const stored = localStorage.getItem("userDetails");
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.id || parsed?.user_id || null;
    }
  } catch (e) {
    // ignore
  }
  return null;
};

export const trackEvent = (eventName, eventCategory, properties = {}) => {
  if (!isInitialized) return;

  const event = {
    event_name: eventName,
    event_category: eventCategory,
    properties,
    user_id: getUserId(),
    session_id: getSessionId(),
    page_url: window.location.href,
    referrer_url: document.referrer || null,
    timestamp: new Date().toISOString().slice(0, 19).replace("T", " "),
  };

  eventQueue.push(event);

  if (eventQueue.length >= MAX_BATCH_SIZE) {
    flushEvents();
  }
};

export const trackPageView = (pageName, extraProps = {}) => {
  trackEvent("page_view", "navigation", {
    page_name: pageName,
    page_path: window.location.pathname,
    ...extraProps,
  });
};

export const trackJobView = (jobId, jobTitle, companyName) => {
  trackEvent("job_view", "engagement", {
    job_id: jobId,
    job_title: jobTitle,
    company_name: companyName,
  });
};

export const trackJobSave = (jobId, jobTitle) => {
  trackEvent("job_save", "engagement", { job_id: jobId, job_title: jobTitle });
};

export const trackJobLike = (jobId, jobTitle) => {
  trackEvent("job_like", "engagement", { job_id: jobId, job_title: jobTitle });
};

export const trackJobApply = (jobId, jobTitle) => {
  trackEvent("job_apply", "engagement", { job_id: jobId, job_title: jobTitle });
};

export const trackMatchScore = (jobTitle, score) => {
  trackEvent("match_score_run", "engagement", {
    job_title: jobTitle,
    score,
  });
};

export const trackResumeUpload = () => {
  trackEvent("resume_upload", "profile", {});
};

export const trackProfileUpdate = (fields = []) => {
  trackEvent("profile_update", "profile", { updated_fields: fields });
};

const flushEvents = () => {
  if (eventQueue.length === 0) return;

  const batch = eventQueue.splice(0, MAX_BATCH_SIZE);

  const payload = { events: batch };

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], {
        type: "application/json",
      });
      navigator.sendBeacon(`${API_URL}/events/track`, blob);
    } else {
      fetch(`${API_URL}/events/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch (e) {
    // Silently fail -- analytics should never break the app
  }
};

export const initAnalytics = () => {
  if (isInitialized) return;
  isInitialized = true;

  flushTimer = setInterval(flushEvents, FLUSH_INTERVAL_MS);

  window.addEventListener("beforeunload", flushEvents);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushEvents();
    }
  });

  trackPageView("initial_load");
};

export const shutdownAnalytics = () => {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  flushEvents();
  isInitialized = false;
};

export default {
  init: initAnalytics,
  shutdown: shutdownAnalytics,
  track: trackEvent,
  pageView: trackPageView,
  jobView: trackJobView,
  jobSave: trackJobSave,
  jobLike: trackJobLike,
  jobApply: trackJobApply,
  matchScore: trackMatchScore,
  resumeUpload: trackResumeUpload,
  profileUpdate: trackProfileUpdate,
};
